import { useShellText } from "../../i18n/useShellText";
import type { CSSProperties, KeyboardEvent } from "react";
import { AppSearch } from "../search/AppSearch";
import { LanguageMenu } from "../menus/LanguageMenu";
import { NotificationsMenu } from "../menus/NotificationsMenu";
import { ProfileMenu } from "../menus/ProfileMenu";
import { ThemeMenu } from "../menus/ThemeMenu";

type TopBarProps = {
	collapsed: boolean;
	onToggle: () => void;
	mobileNavOpen?: boolean;
	isDesktop?: boolean;
};

export const TopBar = ({ collapsed, onToggle, mobileNavOpen, isDesktop }: TopBarProps) => {
  const { text: ui, t } = useShellText();
	// Desktop: X when collapsed (invites expand)
	// Mobile: X when drawer is open (invites close)
	const showX = isDesktop ? collapsed : (mobileNavOpen ?? false);

	const ariaLabel = isDesktop
		? (collapsed ? ui("Expand navigation") : ui("Collapse navigation"))
		: (mobileNavOpen ? ui("Close navigation") : ui("Open navigation"));

	const ariaExpanded = isDesktop ? !collapsed : (mobileNavOpen ?? false);

	const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onToggle();
		}
	};

	return (
		<header
			className="sticky top-0 z-40 flex items-center gap-2 border-b border-border/50 bg-card/70 px-3 py-2 backdrop-blur-xl sm:gap-3 sm:px-5 sm:py-3"
			style={{ minHeight: "var(--topbar-height)" } as CSSProperties}
		>
			<a
				href="#!"
				role="button"
				tabIndex={0}
				aria-label={ariaLabel}
				aria-expanded={ariaExpanded}
				data-collapsed={showX}
				data-tour="menu"
				className="nav-toggle flex-shrink-0"
				onClick={(event) => {
					event.preventDefault();
					onToggle();
				}}
				onKeyDown={handleKeyDown}
			>
				<span aria-hidden />
			</a>
			<div className="flex-1 min-w-0">
				<AppSearch />
			</div>
			<div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
				<LanguageMenu />
				<ThemeMenu />
				<NotificationsMenu />
				<ProfileMenu />
			</div>
		</header>
	);
};

export default TopBar;
